import React, { useRef, useState } from 'react';
import { Input, Button, Image, Space, Spin } from 'antd';
import { UploadOutlined, DeleteOutlined } from '@ant-design/icons';
import { appFeedback } from '@/shared/feedback/appFeedback';
import { container } from '@/shared/di/container.js';
import { UploadPath } from '../paths/UploadPath.js';
import type { UploadItem } from '../../domain/entities/UploadItem.js';

function uuid(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

interface ImageInputWithUploadProps {
  value?: string;
  onChange?: (url: string) => void;
  remoteFolder?: string;
  bucket?: string;
  placeholder?: string;
  disabled?: boolean;
}

export function ImageInputWithUpload({
  value = '',
  onChange,
  remoteFolder = 'promotions/wheel',
  bucket = 'media',
  placeholder = 'Nhập URL ảnh hoặc tải ảnh từ máy tính...',
  disabled = false,
}: ImageInputWithUploadProps) {
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange?.(e.target.value);
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0 || !files[0]) return;

    const file = files[0];
    setUploading(true);

    try {
      const item: UploadItem = {
        id: uuid(),
        file,
        objectUrl: URL.createObjectURL(file),
        bucket,
        remotePath: UploadPath.buildFilePath(remoteFolder, file.name),
        status: 'pending',
        progress: 0,
        publicUrl: null,
        errorMessage: null,
        createdAt: new Date(),
        retryCount: 0,
        isOfflineQueued: false,
      };

      const publicUrl = await container.upload.uploadFileUseCase(item, () => {});
      onChange?.(publicUrl);
      appFeedback.message('success', 'Tải ảnh lên thành công!');
    } catch (err: any) {
      console.error('Lỗi khi tải ảnh:', err);
      appFeedback.error(err, { description: err?.message || 'Không thể tải ảnh lên, vui lòng thử lại.' });
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleClear = () => {
    onChange?.('');
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <input
        type="file"
        ref={fileInputRef}
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleFileSelect}
      />
      <Space.Compact style={{ width: '100%' }}>
        <Input
          value={value}
          onChange={handleInputChange}
          placeholder={placeholder}
          disabled={disabled || uploading}
          allowClear
        />
        <Button
          icon={uploading ? <Spin size="small" /> : <UploadOutlined />}
          onClick={() => fileInputRef.current?.click()}
          loading={uploading}
          disabled={disabled}
        >
          Tải ảnh
        </Button>
      </Space.Compact>

      {value ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 4 }}>
          <Image
            src={value}
            alt="Preview"
            width={60}
            height={60}
            style={{ objectFit: 'cover', borderRadius: 6, border: '1px solid var(--border, #d9d9d9)' }}
            fallback="data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='60' height='60' viewBox='0 0 60 60'><rect width='60' height='60' fill='%23f5f5f5'/><text x='50%' y='50%' dominant-baseline='middle' text-anchor='middle' fill='%23999' font-size='10'>Lỗi ảnh</text></svg>"
          />
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={handleClear}
            disabled={disabled || uploading}
            size="small"
          >
            Xóa ảnh
          </Button>
        </div>
      ) : null}
    </div>
  );
}
